/**
 * Authenticated fetch wrapper.
 *
 * Installs a one-time monkeypatch on `window.fetch` that attaches the
 * Firebase ID token (`Authorization: Bearer <jwt>`) to every same-origin
 * request whose path starts with `/api/`. This avoids touching the ~30
 * existing fetch() call sites scattered across components.
 *
 * Exceptions (never attach a token, even if signed in):
 *   - `/api/firebase-config` — unprotected by design (bootstrap endpoint)
 *
 * Existing callers' `headers` are preserved; an explicit `Authorization`
 * already set on the request wins over ours (admin auth flows, etc.).
 */

import { getIdToken } from "./firebaseAuth";

const _UNAUTH_PATHS = new Set<string>(["/api/firebase-config"]);

let _installed = false;

export function installAuthFetch(): void {
  if (_installed) return;
  _installed = true;
  const _origFetch = window.fetch.bind(window);

  window.fetch = (async (input: RequestInfo | URL, init?: RequestInit) => {
    // Resolve the URL we're calling so we can decide whether to attach.
    let urlString: string;
    if (typeof input === "string") urlString = input;
    else if (input instanceof URL) urlString = input.toString();
    else urlString = (input as Request).url;

    // Same-origin /api/* only. External URLs are never decorated.
    let isApi = false;
    let parsedPath = "";
    try {
      const u = new URL(urlString, window.location.origin);
      isApi =
        u.origin === window.location.origin &&
        u.pathname.startsWith("/api/") &&
        !_UNAUTH_PATHS.has(u.pathname);
      parsedPath = u.pathname + u.search;
    } catch {
      isApi = false;
    }

    if (!isApi) return _origFetch(input, init);

    // ─── Kit-deploy tenant routing ────────────────────────────────────
    // When VITE_KIT_API_BASE is set, the frontend rewrites every same-origin
    // /api/* call to:   <VITE_KIT_API_BASE>/<tenant_slug>/api/*
    // where tenant_slug comes from the first segment of window.location.pathname
    // (acc-chat-app.web.app/jeanie/... → tenant_slug = "jeanie").
    // The backend container behind Tailscale Funnel sees the call as /api/*
    // (Tailscale strips the path prefix). CORS on the backend allows the
    // Firebase Hosting origin.
    const kitBase = import.meta.env.VITE_KIT_API_BASE;
    let finalInput: RequestInfo | URL = input;
    if (kitBase) {
      const seg = window.location.pathname.split("/").filter(Boolean)[0];
      const tenantSlug = seg && !seg.startsWith("api") ? seg : "";
      if (tenantSlug) {
        const rewrittenUrl = `${kitBase.replace(/\/$/, "")}/${tenantSlug}${parsedPath}`;
        // If input was a Request, rebuild it pointed at the new URL while
        // preserving headers/method/body. If input was a string/URL, swap it.
        if (input instanceof Request) {
          finalInput = new Request(rewrittenUrl, input);
        } else {
          finalInput = rewrittenUrl;
        }
      }
    }

    const token = await getIdToken();
    if (!token) return _origFetch(finalInput, init);

    // Merge headers — never clobber an explicit Authorization the caller
    // already set (admin_auth.py terminal flow, etc.).
    const headers = new Headers(init?.headers || {});
    if (!init?.headers && input instanceof Request) {
      input.headers.forEach((v, k) => {
        if (!headers.has(k)) headers.set(k, v);
      });
    }
    if (!headers.has("Authorization")) {
      headers.set("Authorization", `Bearer ${token}`);
    }
    const nextInit: RequestInit = { ...(init || {}), headers };
    return _origFetch(finalInput, nextInit);
  }) as typeof window.fetch;
}
